import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

const SignaturePad = ({ value = "", onChange, label = "Unterschrift", testid = "signature-pad" }) => {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const [hasInk, setHasInk] = useState(Boolean(value));

    // Initialize canvas + restore existing signature
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#0F172A";
        ctx.lineWidth = 2;

        if (value) {
            const img = new Image();
            img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
            img.src = value;
            setHasInk(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawingRef.current = true;
        const ctx = canvasRef.current.getContext("2d");
        const p = pos(e);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
    };
    const move = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const p = pos(e);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
    };
    const end = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        setHasInk(true);
        const data = canvasRef.current.toDataURL("image/png");
        onChange && onChange(data);
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
        onChange && onChange("");
    };

    return (
        <div className="w-full" data-testid={testid}>
            <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{label}</label>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clear}
                    className="h-7 text-xs text-gray-500"
                    data-testid={`${testid}-clear`}
                >
                    <Eraser size={12} className="mr-1" /> Löschen
                </Button>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-md bg-gray-50 relative">
                <canvas
                    ref={canvasRef}
                    className="signature-canvas w-full h-32 block"
                    onMouseDown={start}
                    onMouseMove={move}
                    onMouseUp={end}
                    onMouseLeave={end}
                    onTouchStart={start}
                    onTouchMove={move}
                    onTouchEnd={end}
                    data-testid={`${testid}-canvas`}
                />
                {!hasInk && (
                    <div className="absolute inset-0 grid place-items-center pointer-events-none text-xs text-gray-400">
                        Hier unterschreiben
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignaturePad;
